import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { localeFromPath } from '@/lib/locale-url';

import enTranslations from './locales/en.json';
import frTranslations from './locales/fr.json';

/**
 * Brainfy AI i18n setup.
 *
 * Supported locales: Canadian English (en-CA) + Canadian French (fr-CA).
 * The URL prefix is the source of truth (`/fr`, `/fr/*` → fr-CA, else en-CA),
 * so the 'urlPath' detector runs first and decides the language before the
 * first render. localStorage → <html lang> → navigator only apply where there
 * is no window.location. LocaleSync can't do this job alone: it switches the
 * language in an effect, and that runs before sibling components' useTranslation
 * effects subscribe to 'languageChanged'. Footer and similar components would
 * then keep the wrong language until something else re-rendered them.
 *
 * Legacy 'en' / 'fr' bundles are mapped to the en-CA / fr-CA codes so
 * existing t('...') calls keep working without a locale-string migration.
 */

export const SUPPORTED_LOCALES = ['en-CA', 'fr-CA'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = 'en-CA';
export const LANGUAGE_STORAGE_KEY = 'bf-locale';

const resources = {
  'en-CA': { translation: enTranslations },
  'fr-CA': { translation: frTranslations },
  // Back-compat: some pages were authored against the short codes.
  en: { translation: enTranslations },
  fr: { translation: frTranslations },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: 'urlPath',
  lookup: () =>
    typeof window === 'undefined' ? undefined : localeFromPath(window.location.pathname),
});

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    supportedLngs: ['en-CA', 'fr-CA', 'en', 'fr'],
    fallbackLng: 'en-CA',
    load: 'currentOnly',
    interpolation: { escapeValue: false },
    detection: {
      order: ['urlPath', 'localStorage', 'htmlTag', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

export const normalizeLocale = (raw: string | undefined | null): SupportedLocale => {
  if (!raw) return DEFAULT_LOCALE;
  const lower = raw.toLowerCase();
  if (lower.startsWith('fr')) return 'fr-CA';
  return 'en-CA';
};

export default i18n;
